/**
 * Property palette — a hotel's colours tint the guest portal without ever
 * costing legibility.
 *
 * Properties pick brand colours for print, not for UI: pale sand accents and
 * near-black primaries are both common. So every colour is nudged until it
 * clears WCAG contrast against the surface it will actually sit on, and the
 * AllRound defaults are used when a colour can't be made to work.
 */
const CREAM = "#F7F1E5";
const DEFAULTS = {
  "--lux-gold": "#C6A15B",
  "--lux-gold-deep": "#A6813C",
  "--lux-sand": "#EFE6D2",
};

export function hexToRgb(hex) {
  const m = /^#?([a-f\d]{3}|[a-f\d]{6})$/i.exec((hex || "").trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

const toHex = ({ r, g, b }) =>
  `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v)))
    .toString(16).padStart(2, "0")).join("")}`;

function luminance({ r, g, b }) {
  const f = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function contrast(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

const mix = (a, b, weight) => ({
  r: a.r * weight + b.r * (1 - weight),
  g: a.g * weight + b.g * (1 - weight),
  b: a.b * weight + b.b * (1 - weight),
});

function rgbToHsl({ r, g, b }) {
  const [rr, gg, bb] = [r / 255, g / 255, b / 255];
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  const d = max - min;
  if (!d) return { h: 0, s: 0, l };
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rr) h = ((gg - bb) / d + (gg < bb ? 6 : 0)) / 6;
  else if (max === gg) h = ((bb - rr) / d + 2) / 6;
  else h = ((rr - gg) / d + 4) / 6;
  return { h, s, l };
}

function hslToRgb({ h, s, l }) {
  if (!s) return { r: l * 255, g: l * 255, b: l * 255 };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return { r: hue(h + 1 / 3) * 255, g: hue(h) * 255, b: hue(h - 1 / 3) * 255 };
}

/**
 * Deepen a colour until it reads on cream — in HSL, so a pale sand becomes a
 * rich tan rather than the grey a naive RGB scale would give. Very washed-out
 * brand colours get their saturation floored so they still read as a colour.
 */
function readableOnCream(rgb, target) {
  const cream = hexToRgb(CREAM);
  const hsl = rgbToHsl(rgb);
  const s = hsl.s < 0.32 && hsl.s > 0.02 ? 0.42 : hsl.s;
  for (let l = hsl.l; l >= 0.08; l -= 0.02) {
    const candidate = hslToRgb({ h: hsl.h, s, l });
    if (contrast(candidate, cream) >= target) return candidate;
  }
  return null;
}

/**
 * CSS custom properties for a property's branding, or null when the property
 * has no usable colours (in which case the house palette stands).
 */
export function derivePalette(branding) {
  if (!branding) return null;
  const accent = hexToRgb(branding.accent_color);
  const primary = hexToRgb(branding.primary_color);
  const source = accent || primary;
  if (!source) return null;

  const cream = hexToRgb(CREAM);
  // Accent carries eyebrows, rules and decorative marks. The house gold sits
  // at 2.15:1 on cream, so that is the bar — anything stricter would make
  // every property look heavier than the design intends.
  const gold = readableOnCream(source, 2.15);
  if (!gold) return null;
  // The deep variant carries links and button text → hold it to 4.5:1.
  // The deep variant carries links and button labels, where the house pair
  // sits at 3.2:1.
  const deep = readableOnCream(gold, 3.2) || gold;
  // Sand is a wash behind panels: barely-there tint of the primary.
  const sand = mix(primary || gold, cream, 0.12);

  const palette = {
    "--lux-gold": toHex(gold),
    "--lux-gold-deep": toHex(deep),
    "--lux-sand": toHex(sand),
  };
  // A palette identical to the house one isn't worth a style tag.
  const changed = Object.keys(palette).some((k) => palette[k].toLowerCase() !== DEFAULTS[k].toLowerCase());
  return changed ? palette : null;
}
